import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import api from '../utils/api';

const SchoolSignupPage = () => {
  const [form, setForm] = useState({
    name: '', cbse_affiliation_no: '', principal_name: '',
    email: '', phone: '', address: '', city: '', state: '', pincode: '',
    password: '', confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { error } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.cbse_affiliation_no || !form.email) {
      return error('School name, CBSE affiliation number and email are required.');
    }
    if (!form.password || form.password.length < 8) {
      return error('Password must be at least 8 characters.');
    }
    if (form.password !== form.confirm_password) {
      return error('Passwords do not match.');
    }
    setLoading(true);
    try {
      await api.post('/schools/register', form);
      setSubmitted(true);
    } catch (err) {
      error(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const set = (key) => (e) => setForm(p => ({ ...p, [key]: e.target.value }));

  if (submitted) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)', padding: '24px', fontFamily: "'Nunito', sans-serif" }}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '48px', maxWidth: '480px', width: '100%', textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', margin: '0 auto 20px' }}>🏫</div>
          <h2 style={{ fontSize: '22px', fontWeight: 800, marginBottom: '10px' }}>Application Submitted!</h2>
          <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.7, marginBottom: '24px' }}>
            Thank you for registering <strong>{form.name}</strong>. Our team will verify your CBSE affiliation number and contact you at <strong>{form.email}</strong> within 48 hours.
          </p>
          <button onClick={() => navigate('/')} className="btn-primary">Back to Login</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: "'Nunito', sans-serif" }}>
      {/* Header */}
      <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '16px 40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'var(--brand-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Baloo 2', cursive", fontSize: '16px', fontWeight: 800, color: 'white' }}>G</div>
          <div style={{ fontFamily: "'Baloo 2', cursive", fontSize: '16px', fontWeight: 800, color: 'var(--brand-primary)' }}>MyPersonalGuruji</div>
        </div>
        <span style={{ fontSize: '12px', color: 'var(--muted)', cursor: 'pointer' }} onClick={() => navigate('/')}>
          ← Back to login
        </span>
      </div>

      <div style={{ maxWidth: '640px', margin: '40px auto', padding: '0 24px' }}>
        <div style={{ background: 'white', borderRadius: '16px', padding: '36px' }}>
          <div style={{ marginBottom: '28px' }}>
            <h1 style={{ fontSize: '22px', fontWeight: 800, marginBottom: '6px' }}>Register Your School 🏫</h1>
            <p style={{ fontSize: '13px', color: 'var(--muted)', lineHeight: 1.6 }}>
              Partner with MyPersonalGuruji to bring personalised CBSE learning to your students. We'll verify your CBSE affiliation and get you set up within 48 hours.
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* School details */}
            <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--brand-secondary)', marginBottom: '14px', paddingBottom: '6px', borderBottom: '1px solid var(--border)' }}>
              School Information
            </div>

            <div className="form-group">
              <label className="form-label">School Full Name *</label>
              <input className="form-input" type="text" value={form.name} onChange={set('name')} placeholder="e.g. Delhi Public School, Pune" required />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">CBSE Affiliation Number *</label>
                <input className="form-input" type="text" value={form.cbse_affiliation_no} onChange={set('cbse_affiliation_no')} placeholder="e.g. 1100123" required />
                <span className="form-hint">Found on your CBSE affiliation certificate</span>
              </div>
              <div className="form-group">
                <label className="form-label">Principal Name</label>
                <input className="form-input" type="text" value={form.principal_name} onChange={set('principal_name')} placeholder="Principal full name" />
              </div>
            </div>

            <div style={{ height: '1px', background: 'var(--border)', margin: '8px 0 16px' }}></div>
            <div style={{ fontSize: '13px', fontWeight: 800, color: 'var(--brand-secondary)', marginBottom: '14px' }}>
              Contact Details
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">Official School Email *</label>
                <input className="form-input" type="email" value={form.email} onChange={set('email')} placeholder="principal@school.edu.in" required />
              </div>
              <div className="form-group">
                <label className="form-label">School Phone</label>
                <input className="form-input" type="tel" value={form.phone} onChange={set('phone')} placeholder="Phone number" />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Full Address</label>
              <input className="form-input" type="text" value={form.address} onChange={set('address')} placeholder="Street address" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
              <div className="form-group">
                <label className="form-label">City</label>
                <input className="form-input" type="text" value={form.city} onChange={set('city')} placeholder="City" />
              </div>
              <div className="form-group">
                <label className="form-label">State</label>
                <input className="form-input" type="text" value={form.state} onChange={set('state')} placeholder="State" />
              </div>
              <div className="form-group">
                <label className="form-label">Pincode</label>
                <input className="form-input" type="text" value={form.pincode} onChange={set('pincode')} placeholder="Pincode" />
              </div>
            </div>

            {/* Password */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '4px' }}>
              <div className="form-group">
                <label className="form-label">Create Password *</label>
                <input className="form-input" type="password" value={form.password} onChange={set('password')} placeholder="Min. 8 characters" required />
              </div>
              <div className="form-group">
                <label className="form-label">Confirm Password *</label>
                <input className="form-input" type="password" value={form.confirm_password} onChange={set('confirm_password')} placeholder="Repeat password" required />
              </div>
            </div>

            {/* Partnership note */}
            <div style={{ background: 'var(--brand-secondary-light)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginBottom: '20px', fontSize: '11px', color: 'var(--brand-secondary)', lineHeight: 1.6 }}>
              📋 By submitting, you agree to our School Partnership Agreement. The school will be responsible for student data under DPDP Act 2023. A partnership agreement will be shared once your application is approved.
            </div>

            <button type="submit" className="btn-primary btn-full" disabled={loading} style={{ padding: '13px', fontSize: '14px' }}>
              {loading ? 'Submitting...' : '✓ Submit School Application'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SchoolSignupPage;
